import Papa from "papaparse";
import type { ImportGroup } from "@/shared/import/import-groups";

/**
 * Maps file header names to the canonical column names a shore import
 * handler recognises.
 *
 * Mirrors the shore's csv-parser aliases so a CSV that was previously
 * auto-mapped at the server keeps the same canonical resolution when it is
 * pre-mapped in the browser here. The alias table itself lives on the group
 * (see `import-groups.ts`) because payroll and people recognise different
 * columns.
 */

/** Canonical field name to the file header it was matched with, or null. */
export type ColumnMapping = Record<string, string | null>;

function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Best-effort header to field mapping. For each field in the group, looks
 * through the file's column headers and takes an EXACT alias match if one is
 * free, falling back to a substring match in either direction. Each file
 * header is used at most once.
 *
 * The exact pass is what stops a field claiming a neighbour's column. The
 * substring test is bidirectional, and "amountreason" contains "amount", so
 * with a single pass the `amount` field matched whichever of the two columns
 * came FIRST in the user's file: a sheet listing "Amount Reason" before
 * "Amount" silently proposed the reason text as the money column. The mapping
 * step shows its proposal and a reader can correct it, so this was a bad
 * suggestion rather than a silent corruption, but the suggestion is the thing
 * most people accept.
 *
 * `exactAliases` join the exact pass only. They are the short abbreviations
 * ("pan", "esi", "uan") that would otherwise be found inside unrelated
 * headers by the loose pass.
 */
export function suggestMapping(
  group: ImportGroup,
  fileHeaders: ReadonlyArray<string>,
): ColumnMapping {
  const mapping: ColumnMapping = {};
  const used = new Set<string>();
  const aliasesFor = (fieldName: string): string[] =>
    (group.aliases[fieldName] ?? []).map(normalise).concat(normalise(fieldName));
  const exactAliasesFor = (fieldName: string): string[] =>
    aliasesFor(fieldName).concat((group.exactAliases?.[fieldName] ?? []).map(normalise));

  const claim = (
    fieldName: string,
    aliases: readonly string[],
    matches: (normalisedHeader: string, aliases: readonly string[]) => boolean,
  ): void => {
    if (mapping[fieldName] != null) return;
    const match = fileHeaders.find(
      (header) => !used.has(header) && matches(normalise(header), aliases),
    );
    if (match !== undefined) {
      mapping[fieldName] = match;
      used.add(match);
    }
  };

  // Exact first, across ALL fields, before any field is allowed a loose match.
  for (const field of group.fields) {
    claim(field.name, exactAliasesFor(field.name), (header, aliases) => aliases.includes(header));
  }
  for (const field of group.fields) {
    claim(field.name, aliasesFor(field.name), (header, aliases) =>
      aliases.some((a) => header.includes(a) || a.includes(header)),
    );
    mapping[field.name] = mapping[field.name] ?? null;
  }
  return mapping;
}

/** True once every field the group marks required has a column. */
export function requiredFieldsMapped(group: ImportGroup, mapping: ColumnMapping): boolean {
  return group.fields.every((field) => !field.required || mapping[field.name] != null);
}

/**
 * Parse the header row of a CSV. Uses papaparse so quoting, CRLF, and BOM
 * handling match the shore's parser. Returns the header strings, trimmed.
 */
export function parseHeaders(rawCsv: string): string[] {
  const result = Papa.parse<string[]>(rawCsv, {
    header: false,
    preview: 1,
    skipEmptyLines: true,
  });
  const first = result.data[0] ?? [];
  return first.map((header) => header.trim());
}

/**
 * Given the raw CSV and the user-confirmed mapping, rewrite the header row so
 * every column carries its canonical name. Unmapped columns are dropped from
 * every row. Returns CSV text ready to post to the import endpoint.
 *
 * When `group` is passed (it should be the permission-resolved group, see
 * `resolveImportGroup`), two more things happen:
 * - mapping keys that are not a field of the group are dropped, so a stale or
 *   hidden field can never reach the shore and fail the file;
 * - each field's `alsoSendAs` copies are appended as extra columns carrying
 *   the same values, unless that header is already in the output.
 */
export function rewriteCsvWithMapping(
  rawCsv: string,
  mapping: ColumnMapping,
  group?: ImportGroup,
): string {
  const parsed = Papa.parse<string[]>(rawCsv, {
    header: false,
    skipEmptyLines: "greedy",
  });
  const rows = parsed.data;
  if (rows.length === 0) return rawCsv;
  const fileHeaders = (rows[0] ?? []).map((header) => header.trim());
  const fieldsByName = group ? new Map(group.fields.map((field) => [field.name, field])) : null;

  // File column index to canonical column name, for the mapped columns only.
  const keep: { index: number; field: string }[] = [];
  const copies: { index: number; field: string }[] = [];
  for (const [field, fileColumn] of Object.entries(mapping)) {
    if (!fileColumn) continue;
    if (fieldsByName && !fieldsByName.has(field)) continue;
    const index = fileHeaders.indexOf(fileColumn);
    if (index < 0) continue;
    keep.push({ index, field });
    for (const copy of fieldsByName?.get(field)?.alsoSendAs ?? []) {
      copies.push({ index, field: copy.name });
    }
  }
  for (const copy of copies) {
    if (!keep.some((column) => column.field === copy.field)) keep.push(copy);
  }

  // Rebuild: the header row carries canonical names, body rows pick the file
  // columns by index in the same order.
  const newRows: string[][] = [keep.map((column) => column.field)];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i] ?? [];
    newRows.push(keep.map((column) => row[column.index] ?? ""));
  }
  return Papa.unparse(newRows, { newline: "\n" });
}
